import { Button, Form, Radio, RadioChangeEvent, Space } from 'antd';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { StepValue } from 'pages/CreateUniProfilePage/types';
import commonS from 'styles/common.module.less';
import s from './s.module.less';
import { EMPTY_PLACEHOLDER, INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE, INSURANCE_TYPE_INSURANCE, PAY_TYPE_TEXT } from 'constants/common';
import { renderChangUI } from 'types/changeHighLight';
import StateSelect from 'components/form/StateSelect';
import { InsuranceSetting, TInsuranceServer } from 'types/common';
import StateInsuranceCard from './components/StateInsuranceCard';
import { NoticeType } from 'antd/es/message/interface';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import Tip from 'components/UniprofileInsuranceTips/tip';

interface IInsurance {
    supportPayType: string;
    insurances: InsuranceSetting[];
}

interface IProps {
    isOps?: boolean,
    initFormValue: IInsurance,
    editable?: boolean,
    showEditButtons: boolean,
    onSubmit: (value: any, step: StepValue) => void,
    onNext?: (value: any) => void,
    onEditableChange?: (enable: boolean, step: StepValue, callback: () => void) => void,
    onShowMessage?: (type: NoticeType, content: string) => void;
}

const transformInsurancesToServerFormat = (insurances: InsuranceSetting[]): TInsuranceServer[] => {
    if (!insurances || insurances.length === 0) {
        return [];
    }

    const result: TInsuranceServer[] = [];

    insurances.forEach((insurance) => {
        const { state, carriers, otherCarriers } = insurance;

        if (carriers && carriers.length > 0) {
            carriers.forEach((carrier) => {
                result.push({
                    state,
                    carrier: carrier.name,
                    plans: carrier.plans,
                    insuranceType: 'common',
                });

                if (carrier.otherPlans && carrier.otherPlans.trim()) {
                    const otherPlans = carrier.otherPlans.split(',').map((plan) => plan.trim()).filter(Boolean);
                    if (otherPlans.length > 0) {
                        result.push({
                            state,
                            carrier: carrier.name,
                            plans: otherPlans,
                            insuranceType: 'other',
                        });
                    }
                }
            });
        }

        if (otherCarriers && otherCarriers.trim()) {
            const otherCarriersList = otherCarriers.split(',').map((item) => item.trim()).filter(Boolean);
            otherCarriersList.forEach((carrier) => {
                result.push({
                    state,
                    carrier,
                    plans: [],
                    insuranceType: 'other',
                });
            });
        }
    });

    return result;
};

const Insurance = forwardRef(({
    initFormValue,
    showEditButtons,
    editable,
    onSubmit,
    onNext,
    onEditableChange,
    isOps,
    onShowMessage,
}: IProps, ref) => {
    const [showInsuranceList, setShowInsuranceList] = useState(false);
    const [enable, setEnable] = useState(!!editable);
    const [stateList, setStateList] = useState<string[]>([]);
    const [insuranceForm] = Form.useForm<IInsurance>();
    const [openCardState, setOpenCardState] = useState<string | null>(null);
    const [setting] = useSettings();
    const stateOption = setting[EnumFields.STATE] || [];

    const handleEnableStatusChange = useCallback((status: boolean) => {
        if (typeof onEditableChange === 'function') {
            onEditableChange(status, StepValue.INSURANCE, () => {
                setEnable(status);
            });
        }
    }, [onEditableChange]);

    useEffect(() => {
        if (initFormValue) {
            insuranceForm.setFieldsValue({
                ...initFormValue,
                supportPayType: initFormValue.supportPayType || INSURANCE_TYPE_CASH,
            });
            const states = initFormValue.insurances?.map((item) => item.state) || [];
            setStateList(states);
            setShowInsuranceList(initFormValue.supportPayType !== INSURANCE_TYPE_CASH);
        }
    }, [initFormValue, insuranceForm]);

    useEffect(() => {
        setEnable(!!editable);
    }, [editable]);

    const { insurances } = initFormValue;

    const getCountryLabel = (country: string) => {
        return stateOption.find((item) => item.value === country)?.label || country;
    };

    const handleRadioChange = useCallback((e: RadioChangeEvent) => {
        setShowInsuranceList(e.target.value !== INSURANCE_TYPE_CASH);
    }, []);

    const handleSubmit = useCallback(() => {
        if (insuranceForm.getFieldValue('supportPayType') !== INSURANCE_TYPE_CASH) {
            if (stateList.length === 0) {
                onShowMessage?.('error', 'Please select at least one state');
                return false;
            }
            const insurancesValue = insuranceForm.getFieldValue('insurances') || [];

            if (!insurancesValue.length
                || insurancesValue.some((item: InsuranceSetting) => !item.carriers.length && !item.otherCarriers)) {
                onShowMessage?.('error', 'Please select at least one carrier for each state.');
                return false;
            }
        }
        return insuranceForm.validateFields().then((values) => {
            const { insurances: formInsurances, ...restValues } = values;
            let data;

            if (values.supportPayType === INSURANCE_TYPE_CASH) {
                data = {
                    ...restValues,
                    stateInsuranceList: [],
                };
            } else {
                const stateInsuranceList = transformInsurancesToServerFormat(formInsurances);

                data = {
                    ...restValues,
                    stateInsuranceList,
                };
            }

            if (typeof onSubmit === 'function') {
                onSubmit(data, StepValue.INSURANCE);
            }
            return true;
        }).catch((e) => {
            console.error(e);
            return false;
        });
    }, [insuranceForm, onSubmit, onShowMessage, stateList]);

    useImperativeHandle(ref, () => ({
        submitForm: handleSubmit,
    }));

    const renderEditButtons = useMemo(() => {
        if (showEditButtons) {
            return !enable ? (
                <Button
                    onClick={() => handleEnableStatusChange(true)}
                >
                    Edit
                </Button>
            ) : (
                <div className={s.editButtons}>
                    <Button
                        onClick={() => handleEnableStatusChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                    >
                        Save changes
                    </Button>
                </div>
            );
        }

        return null;
    }, [enable, handleEnableStatusChange, handleSubmit, showEditButtons]);

    const handleFinish = useCallback((formValues: IInsurance) => {
        let data = {};

        if (formValues.supportPayType === INSURANCE_TYPE_CASH) {
            data = {
                ...formValues,
                insurances: [],
            };
        } else {
            data = {
                ...formValues,
                insurances: transformInsurancesToServerFormat(formValues.insurances),
            };
        }
        onNext?.(data);
    }, [onNext]);

    const handleInsuranceChange = useCallback((updatedInsurance: InsuranceSetting) => {
        const currentInsurances: InsuranceSetting[] = insuranceForm.getFieldValue('insurances') || [];
        const updatedInsurances = currentInsurances.map((insurance: InsuranceSetting) =>
            (insurance.state === updatedInsurance.state ? updatedInsurance : insurance),
        );

        if (!updatedInsurances.some((ins: InsuranceSetting) => ins.state === updatedInsurance.state)) {
            updatedInsurances.push(updatedInsurance);
        }

        insuranceForm.setFieldsValue({ insurances: updatedInsurances });
    }, [insuranceForm]);

    const toggleCardState = useCallback((state: string) => {
        setOpenCardState((prev) => (prev === state ? null : state));
    }, []);

    const renderSelectInsurance = () => {
        return (
            <Form.Item noStyle shouldUpdate>
                {() => {
                    if (!stateList || stateList?.length === 0) {
                        return null;
                    }
                    const currentInsurances: InsuranceSetting[] = insuranceForm.getFieldValue('insurances') || [];
                    return (
                        <div className={s.module}>
                            <h4>
                                <span>
                                    Please select accepted insurance carriers and plans by state
                                    <i className={s.required} />
                                </span>
                            </h4>
                            <p className={s.description}>
                                Plans are optional, but including them helps more patients find and book with you — and reduces the risk of mismatches.
                            </p>
                            <Tip />
                            <Form.Item label="" name="insurances">
                                {
                                    stateList.map((state) => (
                                        <StateInsuranceCard
                                            key={state}
                                            state={state}
                                            insurance={currentInsurances.find((item: InsuranceSetting) => item.state === state)}
                                            onChange={handleInsuranceChange}
                                            isOpen={openCardState === state}
                                            onToggle={() => toggleCardState(state)}
                                            isOps={isOps}
                                        />
                                    ))
                                }
                            </Form.Item>
                        </div>
                    );
                }
                }
            </Form.Item>
        );
    };

    const renderInsuranceSetting = () => {
        const currentInsurances: InsuranceSetting[] = insuranceForm.getFieldValue('insurances') || [];
        return (
            <>
                <div className={s.module}>
                    <h4>
                        <span>
                            Select states for insurance coverage
                            <i className={s.required} />
                        </span>
                    </h4>
                    <p className={s.description}>
                        Choose the states where you want to set up insurance coverage.
                    </p>
                    <div className={s.row}>
                        <div className={s.displayItem}>

                            <StateSelect
                                className={s.stateSelect}
                                mode="multiple"
                                value={stateList}
                                onChange={(val) => {
                                    setStateList(val);
                                    insuranceForm.setFieldsValue({
                                        insurances: val.map((state: string) => ({
                                            state,
                                            carriers: currentInsurances.find((item) => item.state === state)?.carriers || [],
                                            otherCarriers: currentInsurances.find((item) => item.state === state)?.otherCarriers || '',
                                        })),
                                    });
                                }}
                            />

                        </div>
                    </div>
                </div>
                {renderSelectInsurance()}
            </>
        );
    };

    return (
        <div className={s.module}>
            <div className={s.wrap}>
                <Form
                    id={StepValue.INSURANCE}
                    form={insuranceForm}
                    name={StepValue.INSURANCE}
                    className={`${commonS.formStyle1} ${s.formBody}`}
                    autoComplete="off"
                    layout="vertical"
                    onFinish={handleFinish}
                >
                    <h3>
                        <span>Insurance</span>
                        {renderEditButtons}
                    </h3>
                    {!enable ? (
                        <>
                            <div className={s.row}>
                                <div className={s.displayItem}>
                                    <div className={s.label}>Type</div>
                                    <div className={s.value}>
                                        {PAY_TYPE_TEXT[initFormValue.supportPayType as keyof typeof PAY_TYPE_TEXT]}
                                    </div>
                                    {renderChangUI(
                                        'supportPayType',
                                        initFormValue,
                                        !!isOps,
                                        (val: string) => {
                                            return <div>{PAY_TYPE_TEXT[val as keyof typeof PAY_TYPE_TEXT]}</div>;
                                        },
                                    )}
                                </div>
                            </div>
                            {initFormValue.supportPayType !== INSURANCE_TYPE_CASH && (
                                <>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>
                                                States for insurance coverage
                                            </div>
                                            <div className={s.value}>
                                                <ul>
                                                    {stateList.map((state) => (
                                                        <li key={state} className="mt-1">
                                                            {getCountryLabel(state)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            {renderChangUI(
                                                'insurances',
                                                initFormValue,
                                                !!isOps,
                                                (val: InsuranceSetting[]) => {
                                                    return (
                                                        <ul>
                                                            {val.map((item) => (
                                                                <li key={item.state}>{getCountryLabel(item.state)}</li>
                                                            ))}
                                                        </ul>
                                                    );
                                                },
                                            )}
                                        </div>
                                    </div>
                                    <div className={s.row}>
                                        <div className={s.displayItem}>
                                            <div className={s.label}>
                                                In-network insurance carriers and plan
                                            </div>
                                            <div className={s.value}>
                                                {insurances && insurances.length > 0 ? (
                                                    <ul>
                                                        {insurances?.map((item, i) => (
                                                            <li key={i} className={s.insuranceItem}>
                                                                <div className={s.label} style={{ fontWeight: 500 }}>{getCountryLabel(item.state)}</div>
                                                                {
                                                                    item.carriers.map((carrier, idx) => {
                                                                        return (
                                                                            <div key={idx} className={s.value}>
                                                                                {carrier.plans.length > 0 ? `${carrier.name}: ${carrier.plans.join(', ')}` : carrier.name}
                                                                            </div>
                                                                        );
                                                                    })}

                                                                <div className="mt-3">
                                                                    <p className={s.label}>Other In-Network Insurance Plans</p>
                                                                    {item.carriers.some((carrier) => carrier.otherPlans && carrier.otherPlans.trim()) ? (
                                                                        <>
                                                                            {item.carriers
                                                                                .filter((carrier) => carrier.otherPlans && carrier.otherPlans.trim())
                                                                                .map((carrier, idx) => (
                                                                                    <div key={idx} className={s.value}>
                                                                                        {carrier.name}: {carrier.otherPlans}
                                                                                    </div>
                                                                                ))
                                                                            }
                                                                        </>
                                                                    ) : (
                                                                        <p className={s.value}>{EMPTY_PLACEHOLDER}</p>
                                                                    )}
                                                                </div>

                                                                <div className="mt-3">
                                                                    <p className={s.label}>Other In-Network Insurance carriers</p>
                                                                    {item.otherCarriers && item.otherCarriers.length > 0 ? (
                                                                        <p className={s.value}>{item.otherCarriers}</p>
                                                                    ) : (
                                                                        <p className={s.value}>{EMPTY_PLACEHOLDER}</p>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    EMPTY_PLACEHOLDER
                                                )}
                                            </div>
                                            {renderChangUI(
                                                'insurances',
                                                initFormValue,
                                                !!isOps,
                                                (val: InsuranceSetting[]) => {
                                                    return (
                                                        <ul>
                                                            {val.map((item) => (
                                                                <React.Fragment key={item.state}>
                                                                    <li>{getCountryLabel(item.state)}</li>
                                                                    {
                                                                        item.carriers.map((carrier) => (
                                                                            <div key={`${carrier.name}-container`}>
                                                                                <div>{carrier.name}: {carrier.plans.join(', ')}</div>
                                                                                <div>other plans: {carrier.name}: {carrier.otherPlans}</div>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                    {
                                                                        item.otherCarriers && item.otherCarriers.length > 0 && (
                                                                            <div key={item.otherCarriers}>other carriers: {item.otherCarriers}</div>
                                                                        )
                                                                    }
                                                                </React.Fragment>
                                                            ))}
                                                        </ul>
                                                    );
                                                },
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className={s.module}>
                                <div className={s.row}>
                                    <div className={s.displayItem}>
                                        <Form.Item
                                            // label="Select all insurance companies you are currently in-network "
                                            name="supportPayType"
                                        >
                                            <Radio.Group onChange={handleRadioChange}>
                                                <Space direction="vertical">
                                                    <Radio value={INSURANCE_TYPE_CASH}>
                                                        I only support cash pay.
                                                    </Radio>
                                                    <Radio value={INSURANCE_TYPE_CASH_AND_INSURANCE}>
                                                        I support both cash pay and insurance.
                                                    </Radio>
                                                    <Radio value={INSURANCE_TYPE_INSURANCE}>
                                                        I only accept insurance.
                                                    </Radio>
                                                </Space>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                </div>
                            </div>
                            {showInsuranceList && renderInsuranceSetting()}
                        </>
                    )}
                </Form>
            </div>
        </div>
    );
});

export default Insurance;
